<template>
  <div class="product-gallery">
    <product-badges
      :key="`badges-${product.handle}`"
      class="product-gallery__badges"
      :badges="product.badges"
      :product="product"
      large
    />

    <transition name="fade">
      <button
        v-if="showZoom"
        class="product-gallery__zoom icon-button"
        type="button"
        @click.prevent="handleZoomClick"
      >
        <span
          class="visually-hidden"
          v-text="$string('product.gallery.zoom_in')"
        />

        <icon-zoom />
      </button>
    </transition>

    <transition name="fade">
      <button
        v-if="show3DIcon"
        class="product-gallery__zoom icon-button"
        type="button"
        @click.prevent="handleZoomClick"
      >
        <span
          class="visually-hidden"
          v-text="$string('product.gallery.3d')"
        />

        <icon-3-d />
      </button>
    </transition>

    <product-gallery-carousel
      block-class="product-gallery"
      :brand-tile-background-colour="brandTileBackgroundColour"
      :brand-tiles="brandTiles"
      buttons
      :current-media-position="currentMediaPosition"
      :current-namespace="currentNamespace"
      :config="config"
      :namespace="namespace"
      :product="product"
      :sizes="sizes"
      videos
      @update-current-media-position="handleUpdateCurrentMediaPosition"
    />

    <transition name="fade">
      <button
        v-if="show3DIcon"
        class="product-gallery__3d-button icon-button"
        type="button"
        @click.prevent="handleZoomClick"
      >
        <icon-3-d />
        <p
          class="text-body-xs"
          v-text="$string('product.gallery.3d')"
        />
      </button>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Splide powered carousel of product media.
 *
 * @param {String} brandTileBackgroundColour - Background colour for brand tile.
 * @param {Array} brandTiles - Brand tiles content.
 * @param {Number} currentMediaPosition - Current media position.
 * @param {String} currentNamespace - Current gallery.
 * @param {String} namespace - Namespace.
 * @param {Object} product - Liquid local product object.
 * @emits updateCurrentMediaPosition - Emit carousel media position.
 *
 */
import { mapActions } from 'vuex'

import ProductBadges from '~async/product-badges/product-badges'
import ProductGalleryCarousel from '~async/product-gallery-carousel/product-gallery-carousel'

import IconZoom from '~icons/misc/zoom.svg'
import Icon3D from '~icons/misc/3D.svg'

import breakpoints from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    IconZoom,
    Icon3D,
    ProductBadges,
    ProductGalleryCarousel,
  },

  props: {
    brandTileBackgroundColour: {
      type: String,
      default: '#f4f4f4',
    },
    brandTiles: {
      type: Array,
      default: () => [],
    },
    currentMediaPosition: {
      type: Number,
      default: 1,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
    },
    namespace: {
      type: String,
      default: 'modal',
    },
    product: {
      type: Object,
      default: () => ({
        badges: [],
        images: [],
        media: [],
      }),
    },
  },

  emits: {
    'update-current-media-position': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      config: {
        arrows: false,
        pagination: true,
        start: 0,
      },
      localSlideIndex: 0,
    }
  },

  computed: {

    /**
     * Compute whether to show zoom icon.
     * - Icon is hidden when video media is active.
     * @returns {Boolean}
     */
    showZoom() {
      const currentMedia = this.product.media?.find((media) => {
        return media.position === this.currentMediaPosition
      })

      if (!currentMedia) {
        return false
      }

      return currentMedia.mediaType === 'image'
    },

    /**
     * Compute whether to show 3d icon.
     * - Icon is show up when model media is active.
     * @returns {Boolean}
     */
    show3DIcon() {
      const currentMedia = this.product.media?.find((media) => {
        return media.position === this.currentMediaPosition
      })

      if (!currentMedia) {
        return false
      }

      return currentMedia.mediaType === 'model'
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 33vw, 100vw`
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Emit update current media position event.
     * @param {Number} newPosition - New media position.
     */
    handleUpdateCurrentMediaPosition(newPosition) {
      this.$emit('update-current-media-position', newPosition)
    },

    /**
     * Handle zoom click.
     */
    handleZoomClick() {
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
